import {
  countConditions,
  emptyFilter,
  isCondition,
  validateFilter,
  type FieldDefinition,
  type Filter,
  type FilterCondition,
  type FilterGroup,
} from "@estelle/domain";
import { Search, SlidersHorizontal, X } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { AdvancedFilterEditor } from "@/components/filters/AdvancedFilterEditor";
import { CombinatorToggle } from "@/components/filters/CombinatorToggle";
import { ConditionEditor } from "@/components/filters/ConditionEditor";
import {
  describeConditionParts,
  describeGroup,
  flatConditions,
} from "@/components/filters/describe";
import { MobileFilterSheet } from "@/components/filters/MobileFilterSheet";
import { removeAt, updateAt } from "@/components/filters/tree";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/** Same pause as the search box it replaces — long enough to finish a word. */
const DEBOUNCE_MS = 250;

/**
 * The search box, with the filters in force as pills inside it (#349, #633).
 *
 * The box searches text and nothing else. It used to double as the way to
 * add a filter — typing dropped down a list of fields — and, while someone
 * typed "pay" to find Payment status, applied "pay" as a search behind the
 * list, so the rows changed under them before they had chosen anything. A
 * filter is now added with "+ Filter" or one of the common chips under the
 * box (`CommonFilters`); here the text only ever searches.
 *
 * Each condition in force is a solid pill: clicking it edits that condition
 * alone, its × removes it. Between the pills is the root's AND or OR, written
 * out and one click from the other (`CombinatorToggle`), and a group inside
 * the filter is one pill reading the group out in brackets (#492), which
 * opens the tree editor — the one place a group is edited.
 *
 * Below `md` a pill opens the bottom sheet straight onto its field instead of
 * a popover, and a filter with an OR or a group in it is one summary pill for
 * the tree editor, since the sheet edits one field at a time.
 */
export function FilterInput({
  value,
  fields,
  q,
  onChangeFilter,
  onChangeSearch,
  onClearAll,
  placeholder = "Search",
  common,
  showLabel,
}: {
  value: Filter;
  fields: FieldDefinition[];
  q: string | undefined;
  onChangeFilter: (next: Filter) => void;
  onChangeSearch: (next: string | undefined) => void;
  /**
   * Clear the filter and the search in one navigation (#534). Two calls to
   * `onChangeFilter` then `onChangeSearch` each build their URL from the
   * values they closed over, so the second wrote back the filter the first
   * had just cleared. Without it, `clearAll` falls back to those two calls.
   */
  onClearAll?: () => void;
  placeholder?: string;
  /** The list's common filters, first in the phone's sheet (#633). */
  common?: FieldDefinition[];
  /** The phone sheet's closing button: "Show 78 orders". */
  showLabel?: string;
}) {
  const isMobile = useIsMobile();

  const committed = q ?? "";
  const [draft, setDraft] = useState(committed);
  const lastCommitted = useRef(committed);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [chipAdvancedOpen, setChipAdvancedOpen] = useState(false);

  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileField, setMobileField] = useState<string | undefined>(undefined);

  function openMobileSheet(fieldName?: string) {
    setMobileField(fieldName);
    setMobileSheetOpen(true);
  }

  const inputRef = useRef<HTMLInputElement>(null);

  // A link opened cold, the Back button, and clearing all restore `q` from
  // outside this component — the draft has to follow, same as `SearchBox`.
  useEffect(() => {
    if (lastCommitted.current !== committed) {
      lastCommitted.current = committed;
      setDraft(committed);
    }
  }, [committed]);

  const conditions = flatConditions(value);
  const total = countConditions(value);

  /*
    What is wrong with each chip's clause, by the chip's position (#518). A
    range that runs backwards, or has one end empty, matches nothing under a chip
    that otherwise reads as fine; the chip says so, and the list's "Nothing
    matches" repeats it.
  */
  const problems = useMemo(
    () => validateFilter(value, fields),
    [value, fields],
  );
  const problemOf = (index: number): string | undefined =>
    problems.find((p) => p.path.length === 1 && p.path[0] === index)?.message;
  const groupHasProblem = (index: number): boolean =>
    problems.some((p) => p.path.length > 1 && p.path[0] === index);

  // The text is a search, always: committed after the usual pause.
  useEffect(() => {
    if (draft === committed) return;
    const timer = setTimeout(() => {
      lastCommitted.current = draft;
      onChangeSearch(draft.trim() === "" ? undefined : draft);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [draft, committed, onChangeSearch]);

  function commitSearch() {
    lastCommitted.current = draft;
    onChangeSearch(draft.trim() === "" ? undefined : draft);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitSearch();
      return;
    }
    if (event.key === "Escape") {
      if (draft !== committed) setDraft(committed);
      return;
    }
    if (event.key === "Backspace" && draft === "") {
      // Walks back one condition pill. A group pill is not walked back over:
      // it holds several conditions, and taking them all with one stray key
      // is what its own editor is for instead.
      const last = value.children[value.children.length - 1];
      if (last && isCondition(last)) {
        onChangeFilter(removeAt(value, [value.children.length - 1]));
      }
    }
  }

  const showClear = total > 0 || committed !== "";

  function clearAll() {
    setDraft("");
    lastCommitted.current = "";
    if (onClearAll) {
      onClearAll();
    } else {
      onChangeFilter(emptyFilter());
      if (committed !== "") onChangeSearch(undefined);
    }
    inputRef.current?.focus();
  }

  /** Flip the root's AND/OR: what the connector between two chips does. */
  function toggleCombinator() {
    onChangeFilter({
      ...value,
      combinator: value.combinator === "and" ? "or" : "and",
    });
  }

  // Solid, so a filter in force reads as one at a glance (#633).
  const chipClassName =
    "border-primary/40 bg-primary/10 text-foreground hover:bg-primary/15 inline-flex shrink-0 items-center gap-1 rounded-full border py-0.5 pr-1 pl-2.5 text-xs whitespace-nowrap transition-colors";

  /** The × that removes one condition, inside its pill. */
  function removeButton(index: number, label: string) {
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={`Remove ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          onChangeFilter(removeAt(value, [index]));
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onChangeFilter(removeAt(value, [index]));
          }
        }}
        className="hover:bg-primary/20 text-muted-foreground hover:text-foreground ml-0.5 inline-flex size-5 items-center justify-center rounded-full"
      >
        <X className="size-3" aria-hidden />
      </span>
    );
  }

  function conditionChip(condition: FilterCondition, index: number) {
    const parts = describeConditionParts(condition, fields);
    // A field this section no longer declares — a saved view from
    // before a rename. `applyColumns` drops a stale column the
    // same way rather than rendering a placeholder.
    if (!parts) return null;

    const key = `${condition.field}-${condition.operator}-${String(index)}`;
    const problem = problemOf(index);
    const chip = (
      <button
        key={key}
        type="button"
        title={problem}
        onClick={
          isMobile
            ? () => {
                openMobileSheet(condition.field);
              }
            : undefined
        }
        className={cn(
          chipClassName,
          problem && "border-destructive bg-destructive/10 text-destructive",
        )}
      >
        <span>{parts.head}</span>
        {parts.value !== null ? (
          <span
            className={cn(
              "font-semibold",
              problem ? "text-destructive" : "text-primary-strong",
            )}
          >
            {parts.value}
          </span>
        ) : null}
        {problem ? <span className="sr-only">. {problem}</span> : null}
        {removeButton(index, parts.head)}
      </button>
    );

    // Below `md` there is nowhere for a popover to float beside a
    // chip that already spans most of the screen's width — the
    // sheet is the only editor with room for itself.
    if (isMobile) return chip;

    return (
      <Popover
        key={key}
        open={editingIndex === index}
        onOpenChange={(open) => {
          setEditingIndex(open ? index : null);
        }}
      >
        <PopoverTrigger asChild>{chip}</PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <ConditionEditor
            condition={condition}
            fields={fields}
            submitLabel="Update"
            onSubmit={(next) => {
              onChangeFilter(updateAt(value, [index], () => next));
              setEditingIndex(null);
            }}
            onRemove={() => {
              onChangeFilter(removeAt(value, [index]));
              setEditingIndex(null);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }

  /**
   * A group inside the filter, as one chip reading the group out — "(Shipped
   * is between … and …)" — rather than hiding it behind a count (#492).
   * Opens the tree editor, which is the one place a group can be edited.
   */
  function groupChip(group: FilterGroup, index: number) {
    const text = describeGroup(group, fields);

    return (
      <Popover
        key={`group-${String(index)}`}
        open={editingIndex === index}
        onOpenChange={(open) => {
          setEditingIndex(open ? index : null);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            title={text}
            className={cn(
              chipClassName,
              groupHasProblem(index) &&
                "border-destructive bg-destructive/10 text-destructive",
            )}
          >
            <SlidersHorizontal className="size-3" aria-hidden />
            <span>{text === "" ? "(no conditions)" : `(${text})`}</span>
            {groupHasProblem(index) ? (
              <span className="sr-only">
                . A condition in it is incomplete.
              </span>
            ) : null}
            {removeButton(index, "this group")}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <AdvancedFilterEditor
            value={value}
            fields={fields}
            onApply={onChangeFilter}
            onClose={() => {
              setEditingIndex(null);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }

  let chips: ReactNode;
  if (isMobile) {
    // A flat AND is a pill per condition, each opening the sheet on its field,
    // and anything else is one summary pill for the tree editor. The sheet
    // edits one field at a time and has no way to show an OR or a group, so no
    // pill is offered that it could not open.
    chips =
      conditions === null ? (
        <Popover open={chipAdvancedOpen} onOpenChange={setChipAdvancedOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={chipClassName}>
              <SlidersHorizontal className="size-3" aria-hidden />
              {total} {total === 1 ? "condition" : "conditions"}
              {value.combinator === "or" ? ", any" : ""}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-3">
            <AdvancedFilterEditor
              value={value}
              fields={fields}
              onApply={onChangeFilter}
              onClose={() => {
                setChipAdvancedOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      ) : (
        conditions.map((condition, index) => conditionChip(condition, index))
      );
  } else {
    // Every child of the root is a pill, with the root's AND or OR written
    // between them and one click from the other. A filter with an OR or a
    // group in it used to collapse to "N conditions (advanced)", which hid
    // the very thing that made #492's list wrong.
    const shown: ReactNode[] = [];
    value.children.forEach((child, index) => {
      const chip = isCondition(child)
        ? conditionChip(child, index)
        : groupChip(child, index);
      if (chip === null) return;
      if (shown.length > 0) {
        shown.push(
          <CombinatorToggle
            key={`join-${String(index)}`}
            combinator={value.combinator}
            onToggle={toggleCombinator}
          />,
        );
      }
      shown.push(chip);
    });
    chips = shown;
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5">
      <div className="relative min-w-0 flex-1">
        <div
          className={cn(
            "border-input focus-within:ring-ring flex h-9 min-w-0 items-center rounded-md border pr-1 pl-2",
            "focus-within:ring-2",
          )}
        >
          <Search
            aria-hidden
            className="text-muted-foreground size-4 shrink-0"
          />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-1 pl-1.5 [scrollbar-width:none]">
            {chips}
            <input
              ref={inputRef}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              onKeyDown={onKeyDown}
              placeholder={total > 0 ? "" : placeholder}
              aria-label={placeholder}
              className="h-6 min-w-24 flex-1 border-0 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {showClear ? (
            <button
              type="button"
              aria-label="Clear filters and search"
              onClick={clearAll}
              className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring shrink-0 rounded-full p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      {isMobile ? (
        <MobileFilterSheet
          open={mobileSheetOpen}
          onOpenChange={setMobileSheetOpen}
          fields={fields}
          common={common}
          value={value}
          initialField={mobileField}
          onChange={onChangeFilter}
          showLabel={showLabel}
        />
      ) : null}
    </div>
  );
}
